/**
 * What a denied merchant is told on the paywall, as a KIND.
 *
 * No i18n and no formatters: `components/billing/gateCopy.js` maps the
 * KIND onto the paywall's translation keys.
 *
 * The ORDER is the load-bearing part. `needsResubscribe` is the only fact
 * that says this shop has ever had a paid subscription
 * (`billing/services/access.py` — it reads `subscription_started_at`), so
 * every sentence that names one is gated behind it. `blocked` and
 * `payment_lapsed` are both reachable by a merchant who never subscribed:
 * an access-grace row whose grace expired derives to one or the other
 * depending only on whether a reconcile has blocked it yet.
 *
 * The free period gets ONE sentence, and only for `trial_expired` — the
 * status that says the shop's evaluation window closed with nothing
 * approved. No date, no day count.
 *
 * `test_subscription` reads the same sentence: a development store
 * approved a test charge, which never bills, and its evaluation has ended
 * — that IS the free period closing, and the ladder is what every closed
 * evaluation sees. The subscribe click refuses it
 * (`dev_store_evaluation_closed`, `billing/services/subscribe.py`). It is
 * decided ahead of `needsResubscribe`, which is true for it (the approval
 * was stamped like any other) and would otherwise call it a lapsed renewal.
 */

/**
 * The sentences a denied merchant can be shown. The caller owns the
 * wording; this names which one is true.
 */
export const PAYWALL_COPY = {
  /** The platform paused billing after failed payments. Fix the card. */
  PAYMENT_ON_HOLD: 'payment_on_hold',
  /** Had a subscription; it is no longer active. Resume it. */
  BLOCKED: 'blocked',
  /** Had a subscription; the latest renewal did not confirm. */
  LAPSED: 'lapsed',
  /**
   * The evaluation window closed with nothing to bill: never subscribed
   * (`trial_expired`), or a development store's test subscription
   * (`test_subscription`).
   */
  TRIAL_ENDED: 'trial_ended',
  /** Never subscribed, with nothing to announce. Just the ladder. */
  CHOOSE: 'choose',
};

/**
 * Which sentence this merchant should read.
 *
 * @param {object}  input
 * @param {string}  input.status            backend state vocabulary
 *   (`billing.domain.states.SubscriptionState` + trial/trial_expired/fresh)
 * @param {boolean} input.needsResubscribe  has this shop ever had a paid
 *   subscription (`needs_resubscribe` off the wire)
 * @returns {string} a PAYWALL_COPY value
 */
export function paywallCopyKind({ status, needsResubscribe }) {
  // A development store's evaluation ended; nothing it approved bills, so
  // the renewal branch below is not its story. Ahead of everything, as
  // the backend derives it ahead of blocked and the payment hold.
  if (status === 'test_subscription') return PAYWALL_COPY.TRIAL_ENDED;

  // A platform payment hold is a statement about a card, not about a
  // subscription the merchant may never have had — and it is unreachable
  // without one, since only an approved subscription can be frozen. It
  // stays ahead of everything for both reasons.
  if (status === 'payment_on_hold') return PAYWALL_COPY.PAYMENT_ON_HOLD;

  if (needsResubscribe) {
    // `blocked` is "the subscription you had is not running"; every other
    // denied state for a former subscriber is a renewal that did not
    // confirm. Both are true sentences only on this side of the branch.
    return status === 'blocked' ? PAYWALL_COPY.BLOCKED : PAYWALL_COPY.LAPSED;
  }

  // Never subscribed on this shop. They are choosing a plan, not resuming
  // one. The evaluation window closing is the only thing worth stating, and
  // only for the status that says so — a grace that ran out (`blocked`,
  // `payment_lapsed`) or a store with no anchor at all (`fresh`) gets the
  // ladder alone.
  return status === 'trial_expired' ? PAYWALL_COPY.TRIAL_ENDED : PAYWALL_COPY.CHOOSE;
}
